import { IModel, INewModel } from '../chatbot/api/llmapi';
import { Conversation } from './conversation';
import { NewModelInfo, NewModelDialog } from '../dialogs/newmodeldialog';
import { ModelParametersDialog } from '../dialogs/modelparameters';
import { SpinnerManager } from './spinnermanager';

type ModelListUpdateCallback = (list: IModel[], selected?: IModel) => void;

export class ModelList {
    static listOfModels  : IModel[] | undefined;
    static selectedModel : IModel | undefined;
    static callbacks     : Set<ModelListUpdateCallback> = new Set<ModelListUpdateCallback>();

    static addModel(model : IModel) : void
    {
        ModelList.listOfModels!.push(model);
        ModelList.setModels(ModelList.listOfModels!);
    }

    static getModelList() : IModel[]
    {
        if (ModelList.listOfModels === undefined) {
            return [];
        }
        else {
            return ModelList.listOfModels;
        }
    }

    static async deleteModel(model : IModel)
    {
        try {
            if (model.read_only) {
                return;
            }

            await Conversation.api.deleteModel!(model);
            
            ModelList.removeModel(model);

            if (ModelList.selectedModel === model) {
                ModelList.selectDefaultModel();
            }
        }
        catch (error) {
            console.log((error as Error).message);
        }
    }

    static getModelByName(name : string) : IModel | undefined
    {
        const list = ModelList.listOfModels!;
        for (const model of list) {
            if (model.name === name) {
                return model;
            }
        }

        return undefined;
    }

    static async editModel()
    {
        try {
            await ModelParametersDialog.showDialog(ModelList.selectedModel!);
        }
        catch (error) {
            console.log((error as Error).message);
        }
    }

    static async loadModels()
    {
        if (ModelList.listOfModels === undefined) {
            ModelList.setModels(await Conversation.api.modelList!());

            const list = ModelList.listOfModels!;
            if (ModelList.selectedModel === undefined && list !== undefined) {
                for (const model of list) {
                    if (model.default_model) {
                        ModelList.selectModel(model);
                    }
                }
            }
        }
    }

    static async createModel() : Promise<void>
    {
        try {
            const data     : NewModelInfo  = await NewModelDialog.showDialog();
            const newModel : INewModel = {
                name        : data.name,
                description : data.description,
                parent_model : data.selectedModel
            };
            if (data.rank !== undefined) {
                newModel.rank = data.rank;
            }

            SpinnerManager.setState(true);
            const createdModel = await Conversation.api.createModel!(newModel);            
            ModelList.addModel(createdModel);
            SpinnerManager.setState(false);

            const model = ModelList.getModelByName(data.name);
            if (model !== undefined) {
                ModelList.selectModel(model);
            }
        }
        catch (error) {
            SpinnerManager.setState(false);
            console.log((error as Error).message);
        }
    }

    static onUpdate(updateHandler : ModelListUpdateCallback)
    {
        ModelList.callbacks.add(updateHandler);
    }

    static removeUpdateEvent(updateHandler : ModelListUpdateCallback)
    {
        ModelList.callbacks.delete(updateHandler);
    }

    static reloadModelList(reload : boolean)
    {
        ModelList.listOfModels = undefined;
        if (reload) {
            ModelList.loadModels();
        }
    }

    static removeModel(model : IModel) : void
    {
        ModelList.listOfModels = ModelList.listOfModels!.filter(m => m.id !== model.id);
        ModelList.setModels(ModelList.listOfModels);
    }

    static selectDefaultModel()
    {
        const list = ModelList.listOfModels!;
        if (list !== undefined) {
            for (const model of list) {
                if (model.default_model) {
                    ModelList.selectModel(model);
                }
            }
        }
    }

    static selectModel(model : IModel | undefined) : void 
    {
        console.log("Setting model "+ model);
        
        ModelList.selectedModel = model;

        ModelList.callbacks.forEach(callback => {
            if (ModelList.listOfModels !== undefined) {
                callback(ModelList.listOfModels, ModelList.selectedModel);
            }
        });
    }

    private static setModels(list : IModel[]) : void
    {
        ModelList.listOfModels = list;
        ModelList.callbacks.forEach(callback => {
            if (ModelList.listOfModels !== undefined) {
                callback(ModelList.listOfModels, ModelList.selectedModel);
            }
        });
    }
};

